import { useState, useEffect, useMemo } from 'react'
import { IAPIDataset } from 'extra/types/ApiModels'
import { api } from '../../api/client'
import { useEntities } from '../../context/EntitiesContext'
import { EditableJsonCell } from '../../components/DataDisplayComponents'

export function DatasetSettings ({
  dataset,
  onUpdate,
  onDelete,
}: {
  dataset: IAPIDataset
  onUpdate: (d: IAPIDataset) => void
  onDelete: () => void
}) {
  const [name, setName] = useState(dataset.name)
  const [promptVariantIds, setPromptVariantIds] = useState<Array<string>>(dataset.promptVariantIds || [])
  const [deploymentIds, setDeploymentIds] = useState<Array<string>>(dataset.deploymentIds || [])
  const [evalIds, setEvalIds] = useState<Array<string>>(dataset.evalIds || [])
  const [evalConfigs, setEvalConfigs] = useState<Record<string, any>>(dataset.evalConfigs || {})

  const { variants: allVariants, deployments: allDeployments } = useEntities()
  const availableVariants = useMemo(() => allVariants.filter(v => v.promptId === dataset.promptId), [allVariants, dataset.promptId])
  const availableDeployments = useMemo(() => allDeployments.filter(d => d.promptId === dataset.promptId), [allDeployments, dataset.promptId])
  const [availableEvaluators, setAvailableEvaluators] = useState<Array<string>>([])

  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    api.datasets.getEvaluators()
      .then(setAvailableEvaluators)
      .catch(console.error)
  }, [])

  const autosave = async (fields: Record<string, any>) => {
    try {
      const updated = await api.datasets.update(dataset.id, fields)
      onUpdate(updated)
    } catch (e) {
      console.error('Failed to autosave', e)
    }
  }

  const handleUpdateEvalConfig = async (evalId: string, val: any) => {
    const newConfigs = { ...evalConfigs, [evalId]: val }
    setEvalConfigs(newConfigs)
    autosave({ evalConfigs: newConfigs })
  }

  const handleNameBlur = () => {
    if (name !== dataset.name) {
      autosave({ name })
    }
  }

  const handleDelete = async () => {
    if (!window.confirm(`Delete dataset "${dataset.name}"? This cannot be undone.`)) return
    setDeleting(true)
    try {
      await api.datasets.delete(dataset.id)
      onDelete()
    } catch {
      setError('Failed to delete dataset')
      setDeleting(false)
    }
  }

  const toggleAndSave = (id: string, current: Array<string>, setter: (val: Array<string>) => void, field: string) => {
    const newValue = current.includes(id)
      ? current.filter(i => i !== id)
      : [...current, id]
    setter(newValue)
    autosave({ [field]: newValue })
  }

  return (
    <div className="max-w-2xl space-y-6">
      {error && <div className="alert-error">{error}</div>}

      {/* Name */}
      <div>
        <label className="input-label">Name</label>
        <input
          type="text"
          value={name}
          onChange={e => setName(e.target.value)}
          onBlur={handleNameBlur}
          className="input"
        />
      </div>

      {/* Variants */}
      <div>
        <label className="input-label">Prompt Variants</label>
        <div className="max-h-48 overflow-y-auto border border-gray-700 rounded p-2 bg-gray-900">
          {availableVariants.map(v => (
            <div key={v.id} className="flex items-center gap-2 py-1">
              <input
                type="checkbox"
                checked={promptVariantIds.includes(v.id)}
                onChange={() => toggleAndSave(v.id, promptVariantIds, setPromptVariantIds, 'promptVariantIds')}
              />
              <span className="text-sm">{v.name}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Deployments */}
      <div>
        <label className="input-label">Deployments</label>
        <div className="max-h-48 overflow-y-auto border border-gray-700 rounded p-2 bg-gray-900">
          {availableDeployments.map(d => (
            <div key={d.id} className="flex items-center gap-2 py-1">
              <input
                type="checkbox"
                checked={deploymentIds.includes(d.id)}
                onChange={() => toggleAndSave(d.id, deploymentIds, setDeploymentIds, 'deploymentIds')}
              />
              <span className="text-sm">{d.label || d.id}</span>
            </div>
          ))}
          {availableDeployments.length === 0 && <div className="text-gray-500 text-sm">No deployments found.</div>}
        </div>
      </div>

      {/* Evaluators */}
      <div>
        <label className="input-label">Evaluators</label>
        <div className="max-h-48 overflow-y-auto border border-gray-700 rounded p-2 bg-gray-900">
          {availableEvaluators.map(e => (
            <div key={e} className="flex items-center gap-2 py-1">
              <input
                type="checkbox"
                checked={evalIds.includes(e)}
                onChange={() => toggleAndSave(e, evalIds, setEvalIds, 'evalIds')}
              />
              <span className="text-sm">{e}</span>
            </div>
          ))}
          {availableEvaluators.length === 0 && <div className="text-gray-500 text-sm">No evaluators found.</div>}
        </div>
      </div>

      {/* Eval Configs */}
      <div>
        <label className="input-label">Evaluator Configs</label>
        <div className="flex flex-col gap-4">
          {evalIds.map(evalId => (
            <div key={evalId}>
              <div className="text-sm text-gray-400 mb-1">{evalId}</div>
              <EditableJsonCell
                initialValue={evalConfigs[evalId] || {}}
                onSave={(val) => handleUpdateEvalConfig(evalId, val)}
              />
            </div>
          ))}
          {evalIds.length === 0 && <div className="text-gray-500 text-sm">No evaluators selected.</div>}
        </div>
      </div>

      <div className="flex justify-end pt-4">
        <button
          onClick={handleDelete}
          disabled={deleting}
          className="btn btn-danger-light"
        >
          Delete Dataset
        </button>
      </div>
    </div>
  )
}
